import {
    getBillableEvent,
    listBillableEvents,
    listBillableEventsByChannelAndDay,
    listBillableEventsByState,
} from './queries';

import {GraphQLQuery,} from '@aws-amplify/api';
import {BillableEvent} from "../types.ts";
import {billingApiClient} from "../../billing-api-integration";
import {ListRecordsQuery} from "../../../types";

export type ListBillableEvents = {
    listBillableEvents: { items: BillableEvent[], nextToken: string }
}

export type ListBillableEventsByState = {
    listBillableEventsByState: { items: BillableEvent[], nextToken: string }
}

export type ListBillableEventsByChannelAndDay = {
    listBillableEventsByChannelAndDay: { items: BillableEvent[], nextToken: string }
}

export type GetBillableEvent = {
    getBillableEvent: BillableEvent
}


const client = billingApiClient;

const normalizeFilter = (filter: { [key: string]: string }) =>
    Object.keys(filter).reduce((acc: { [key: string]: string }, key) => {
        if (filter[key] !== '' && filter[key] !== undefined) {
            acc[key] = filter[key];
        }
        return acc;
    }, {});

// FNP-533: BillableEvents are read-only and backed by three list operations.
// fetchRecords routes to the cheapest query that satisfies the active filter:
//
//   1. channel + dayStart + dayEnd present  -> listBillableEventsByChannelAndDay
//      (the channel/day GSI; channel is required, both day bounds are required)
//   2. ONLY state present                   -> listBillableEventsByState
//      (the state GSI; optional createdBefore is forwarded if present)
//   3. anything else                        -> listBillableEvents with filter
//      (base table scan-equivalent list, filter = { customerId, meteringRecordId, state })
//
// Each branch returns the same { items, nextToken } envelope so the infinite
// query hook is agnostic to which query ran.
export const fetchRecords = async (query: ListRecordsQuery) => {
    const {filter, limit, nextToken} = query;
    const normalized = filter ? normalizeFilter(filter as { [key: string]: string }) : {};

    const hasChannelDayRange = Boolean(normalized.channel && normalized.dayStart && normalized.dayEnd);
    const filterKeys = Object.keys(normalized);
    const isStateOnly = filterKeys.length === 1 && Boolean(normalized.state);

    // Branch 1: channel + day-range GSI
    if (hasChannelDayRange) {
        const response = await client.graphql<GraphQLQuery<ListBillableEventsByChannelAndDay>>({
            query: listBillableEventsByChannelAndDay,
            variables: {
                channel: normalized.channel,
                dayStart: normalized.dayStart,
                dayEnd: normalized.dayEnd,
                limit,
                nextToken,
            },
        });
        return {
            // FNP-539: guard against a GraphQL-level error (response.data undefined),
            // so the hook always receives a real array, never an undefined item.
            items: response.data?.listBillableEventsByChannelAndDay.items ?? [],
            nextToken: response.data?.listBillableEventsByChannelAndDay.nextToken,
        };
    }

    // Branch 2: state-only GSI
    if (isStateOnly) {
        const response = await client.graphql<GraphQLQuery<ListBillableEventsByState>>({
            query: listBillableEventsByState,
            variables: {
                state: normalized.state,
                ...(normalized.createdBefore ? {createdBefore: normalized.createdBefore} : {}),
                limit,
                nextToken,
            },
        });
        return {
            // FNP-539: guard against a GraphQL-level error (response.data undefined).
            items: response.data?.listBillableEventsByState.items ?? [],
            nextToken: response.data?.listBillableEventsByState.nextToken,
        };
    }

    // Branch 3: base list with BillableEventsFilterInput { customerId, meteringRecordId, state }
    const {channel: _channel, dayStart: _dayStart, dayEnd: _dayEnd, createdBefore: _createdBefore, ...baseFilter} = normalized;
    const response = await client.graphql<GraphQLQuery<ListBillableEvents>>({
        query: listBillableEvents,
        variables: {
            ...(Object.keys(baseFilter).length ? {filter: baseFilter} : {}),
            ...(query.sort ? {sort: query.sort} : {}),
            limit,
            nextToken,
        },
    });
    return {
        // FNP-539: guard against a GraphQL-level error (response.data undefined).
        items: response.data?.listBillableEvents.items ?? [],
        nextToken: response.data?.listBillableEvents.nextToken,
    };
};

export const fetch = async (id: string) => {
    const response = await client.graphql<GraphQLQuery<GetBillableEvent>>({
        query: getBillableEvent,
        variables: {id: id},
    });
    return response.data?.getBillableEvent;
};
